import React, { useEffect, useState } from "react";
import { Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHead, Panel } from "@/pages/vendor/Shell";
import { ROLE_LABELS } from "@/pages/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const ROLES = Object.keys(ROLE_LABELS);

function RolePicker({ value, onChange, idp }: { value: string[]; onChange: (v: string[]) => void; idp: string }) {
  return (
    <div className="flex flex-wrap gap-3">
      {ROLES.map((r) => (
        <label key={r} className="flex items-center gap-1.5 text-xs">
          <Checkbox checked={value.includes(r)} onCheckedChange={(c) => onChange(c ? [...value, r] : value.filter((x) => x !== r))} data-testid={`${idp}-role-${r}`} />
          {ROLE_LABELS[r]}
        </label>
      ))}
    </div>
  );
}

function StaffRow({ s, me, onSaved }: { s: any; me: string; onSaved: () => void }) {
  const [f, setF] = useState({ name: s.name, platform_roles: s.platform_roles, backup_email: s.backup_email || "", backup_phone: s.backup_phone || "" });
  const save = async (extra: any = {}) => {
    try { await api.patch(`/admin/staff/${s.id}`, { ...f, ...extra }); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/staff/${s.id}`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel className="space-y-3" data-testid={`admin-staff-row-${s.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{s.email} {s.email === me && <span className="text-xs text-primary">(you)</span>}</p>
        <div className="flex items-center gap-3 text-xs">
          Active <Switch checked={!s.suspended} onCheckedChange={(v) => save({ suspended: !v })} data-testid={`admin-staff-active-${s.id}`} />
          <Button size="icon" variant="ghost" onClick={remove} disabled={s.email === me} data-testid={`admin-staff-remove-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" data-testid={`admin-staff-name-${s.id}`} />
        <Input value={f.backup_email} onChange={(e) => setF({ ...f, backup_email: e.target.value })} placeholder="Backup email" data-testid={`admin-staff-backup-email-${s.id}`} />
        <Input value={f.backup_phone} onChange={(e) => setF({ ...f, backup_phone: e.target.value })} placeholder="Backup phone (+1...)" data-testid={`admin-staff-backup-phone-${s.id}`} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <RolePicker value={f.platform_roles} onChange={(v) => setF({ ...f, platform_roles: v })} idp={`admin-staff-${s.id}`} />
        <Button size="sm" onClick={() => save()} data-testid={`admin-staff-save-${s.id}`}>Save</Button>
      </div>
    </Panel>
  );
}

export default function AdminStaff() {
  const { admin } = useAuth();
  const [rows, setRows] = useState<any[]>([]);
  const [f, setF] = useState({ name: "", email: "", platform_roles: ["customer_service"] as string[] });
  const load = () => api.get("/admin/staff").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post("/admin/staff", f); toast.success("Staff member added"); setF({ name: "", email: "", platform_roles: ["customer_service"] }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div>
      <PageHead eyebrow="Admin only" title="Company staff" />
      <p className="mb-4 max-w-2xl text-sm text-muted-foreground">Only admins can add staff or change roles. Account managers handle vendors; customer service handles attendees and consent lookups. Set a backup email or phone so a staff member can still sign in if they lose access to their main email.</p>
      <Panel className="mb-5">
        <form onSubmit={add} className="space-y-3" data-testid="admin-staff-add-form">
          <div className="grid gap-2 sm:grid-cols-2">
            <Input required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-staff-new-name" />
            <Input required type="email" placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-staff-new-email" />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <RolePicker value={f.platform_roles} onChange={(v) => setF({ ...f, platform_roles: v })} idp="admin-staff-new" />
            <Button type="submit" data-testid="admin-staff-add-button"><UserPlus className="mr-1 h-4 w-4" /> Add staff</Button>
          </div>
        </form>
      </Panel>
      <div className="space-y-3">{rows.map((s) => <StaffRow key={`${s.id}-${s.platform_roles.join()}-${s.suspended}`} s={s} me={admin?.user.email} onSaved={load} />)}</div>
    </div>
  );
}
